import _get from 'lodash/get';
import {
  DocumentationCreateInputObject,
  DocumentationRenewalRepetitionEnum,
  DocumentationUpdateInputObject,
} from 'graphql-common';
import { Values } from '@lib/interfaces/form';
import { SelectOption } from '@lib/components/Select/types';
import mergeAndUnlinkMultiSelectValues from '@lib/utils/mergeAndUnlinkMultiSelectValues';
import DocumentFormFieldNames from '@lib/enums/fieldNames/documentFormFieldNames';
import { GetTaskGroupKey } from '../../utils/createTaskGroupKeyResolver';
import buildTaskGroupLinks from '../../utils/buildTaskGroupLinks';

function getMultiSelectLinks(
  values: Values,
  prevValues: Values,
  fieldName: DocumentFormFieldNames,
  options?: { withVisibleViaQr?: boolean },
) {
  const optionsList = (_get(values, fieldName) || []) as Array<
    SelectOption & { visibleViaQr?: boolean }
  >;
  const prevOptions = (_get(prevValues, fieldName) || []) as Array<
    SelectOption & { visibleViaQr?: boolean }
  >;
  const items = optionsList.map(({ value, visibleViaQr }) =>
    options?.withVisibleViaQr && visibleViaQr !== undefined
      ? { id: `${value}`, visibleViaQr }
      : { id: `${value}` },
  );
  const prevItems = prevOptions.map(({ value, visibleViaQr }) =>
    options?.withVisibleViaQr && visibleViaQr !== undefined
      ? { id: `${value}`, visibleViaQr }
      : { id: `${value}` },
  );
  return mergeAndUnlinkMultiSelectValues(prevItems, items);
}

export type DocumentFormAccessFlags = {
  hasCategoriesAccess?: boolean;
  hasSitesAccess?: boolean;
  hasAssetsAccess?: boolean;
  hasTasksAccess?: boolean;
  hasUsersAccess?: boolean;
};

export default function getPreparedDataFromValues(
  values: Values,
  prevValues: Values,
  isEdit?: boolean,
  isPlanLocked?: boolean,
  accessFlags?: DocumentFormAccessFlags,
  getTaskGroupKey?: GetTaskGroupKey,
): DocumentationCreateInputObject | DocumentationUpdateInputObject {
  const {
    hasCategoriesAccess = true,
    hasSitesAccess = true,
    hasAssetsAccess = true,
    hasTasksAccess = true,
    hasUsersAccess = true,
  } = accessFlags ?? {};

  const categoryOption = _get(
    values,
    DocumentFormFieldNames.Category,
    null,
  ) as SelectOption | null;

  const approverOption = _get(
    values,
    DocumentFormFieldNames.Approver,
    null,
  ) as SelectOption | null;

  const documentationTags = getMultiSelectLinks(
    values,
    prevValues,
    DocumentFormFieldNames.Tags,
  );
  const sectorLinks = getMultiSelectLinks(
    values,
    prevValues,
    DocumentFormFieldNames.Sectors,
  );
  const siteLinks = getMultiSelectLinks(
    values,
    prevValues,
    DocumentFormFieldNames.Sites,
  );
  const assetLinks = getMultiSelectLinks(
    values,
    prevValues,
    DocumentFormFieldNames.Equipment,
    { withVisibleViaQr: true },
  );
  const areaLinks = getMultiSelectLinks(
    values,
    prevValues,
    DocumentFormFieldNames.Areas,
    { withVisibleViaQr: true },
  );
  const formTemplateLinks = getMultiSelectLinks(
    values,
    prevValues,
    DocumentFormFieldNames.FormTemplates,
  );
  // Tasks routed through the shared `buildTaskGroupLinks` helper so the
  // defensive Apollo-cache resolver (if provided) can map any per-occurrence
  // Task.id back to its groupKey before submission — same path used by
  // BulkEdit and Import.
  const taskGroupLinks = buildTaskGroupLinks({
    values: _get(values, DocumentFormFieldNames.Tasks) as
      { value: string }[] | undefined,
    prevValues: _get(prevValues, DocumentFormFieldNames.Tasks) as
      { value: string }[] | undefined,
    getTaskGroupKey,
  });

  const renewalEnabled = !!_get(
    values,
    DocumentFormFieldNames.RenewalReminderEnabled,
  );
  let renewalDeadlineDate: string | null | undefined;
  let renewalRepetition: DocumentationRenewalRepetitionEnum | null | undefined;
  let renewalEarlyReminderDays: number | null | undefined;
  let renewalRepetitionCustomDays: number | null | undefined;

  if (renewalEnabled) {
    const deadlineDateRaw = _get(
      values,
      DocumentFormFieldNames.RenewalReminderDeadlineDate,
    ) as string | undefined;
    renewalDeadlineDate = deadlineDateRaw
      ? deadlineDateRaw.split('T')[0]
      : undefined;
    renewalRepetition = _get(values, [
      DocumentFormFieldNames.RenewalReminderRepetition,
      'value',
    ]) as DocumentationRenewalRepetitionEnum | undefined;
    const earlyReminderDaysRaw = _get(
      values,
      DocumentFormFieldNames.RenewalReminderEarlyAmount,
    );
    if (earlyReminderDaysRaw != null && earlyReminderDaysRaw !== '') {
      renewalEarlyReminderDays = parseInt(`${earlyReminderDaysRaw}`, 10);
    } else {
      renewalEarlyReminderDays = null;
    }
    if (renewalRepetition === DocumentationRenewalRepetitionEnum.Custom) {
      const customDaysRaw = _get(
        values,
        DocumentFormFieldNames.RenewalReminderCustomDays,
      );
      renewalRepetitionCustomDays =
        customDaysRaw != null ? parseInt(`${customDaysRaw}`, 10) : undefined;
    } else {
      renewalRepetitionCustomDays = null;
    }
  } else {
    renewalDeadlineDate = null;
    renewalRepetition = null;
    renewalEarlyReminderDays = null;
    renewalRepetitionCustomDays = null;
  }

  // Free-plan (LimitedRead) users: BE rejects any gated argument with
  // UPGRADE_PLAN_REQUIRED. Only un-gated fields can be sent — see
  // `app/graphql/types/inputs/documentations/create_input_object.rb`.
  // Allowed: name, description, code, attachment_attached, documentation_tags,
  // asset_links. Everything else carries `upgrade_feature: :documentation_management`.
  const baseData: DocumentationUpdateInputObject = isPlanLocked
    ? {
        name: (_get(values, DocumentFormFieldNames.Name, '') as string) || null,
        code: (_get(values, DocumentFormFieldNames.Code, '') as string) || null,
        description:
          (_get(values, DocumentFormFieldNames.Description, '') as string) ||
          null,
        ...(documentationTags !== null && { documentationTags }),
        ...(assetLinks !== null && { assetLinks }),
      }
    : {
        name: (_get(values, DocumentFormFieldNames.Name, '') as string) || null,
        code: (_get(values, DocumentFormFieldNames.Code, '') as string) || null,
        description:
          (_get(values, DocumentFormFieldNames.Description, '') as string) ||
          null,
        ...(hasCategoriesAccess && {
          categoryId: _get(categoryOption, 'value', null) as string | null,
        }),
        ...(hasUsersAccess && {
          approverId: (approverOption?.value as string) || null,
        }),
        ...(documentationTags !== null && { documentationTags }),
        ...(hasSitesAccess && sectorLinks !== null && { sectorLinks }),
        ...(hasSitesAccess && siteLinks !== null && { siteLinks }),
        ...(hasAssetsAccess && assetLinks !== null && { assetLinks }),
        ...(hasSitesAccess && areaLinks !== null && { areaLinks }),
        ...(hasTasksAccess &&
          formTemplateLinks !== null && { formTemplateLinks }),
        ...(hasTasksAccess && taskGroupLinks !== null && { taskGroupLinks }),
        renewalDeadlineDate,
        renewalRepetition,
        renewalEarlyReminderDays,
        renewalRepetitionCustomDays,
      };

  if (isEdit) {
    return baseData;
  }

  const attachmentValue = _get(values, DocumentFormFieldNames.Attachment) as
    Record<string, unknown> | undefined;
  const fileId = _get(attachmentValue, ['file', 'id']);
  const attachmentAttached = fileId
    ? ({
        filename: _get(attachmentValue, 'filename'),
        file: _get(attachmentValue, 'file'),
      } as DocumentationCreateInputObject['attachmentAttached'])
    : (undefined as unknown as DocumentationCreateInputObject['attachmentAttached']);

  return {
    ...baseData,
    attachmentAttached,
  } as DocumentationCreateInputObject;
}
